import { ROOF_DECKING_THICKNESS_MM } from './roofDecking';
import { resolveMonoSlope } from './rectRoofGeometry';
import type {
	MaterialRow,
	RoofAssemblyResult,
	RoofConfig,
	RoofRunoffId,
	RoofSystemId,
} from "../../engines/types";
import { getT } from "../../i18n";
import type { Market } from "../location/types";

interface RoofOutlineMetrics {
	slopedAreaSqm: number;
	slopedRunM: number;
	halfSlopeRunM: number;
	takfotsplatLm: number;
	vindskiveplatLm: number;
	nockbeslagLm: number;
}

interface BuildRoofAssemblyInput {
	width: number;
	length: number;
	roofConfig: RoofConfig;
	market?: Market;
}

interface RoofSystemDefinition {
	allowedKinds: Array<"mono" | "gable">;
	layerLabelKey: keyof ReturnType<typeof getT>;
	coveringMaterial: string;
	coveringDimension: string;
}

const WASTE_FACTOR = 1.1;
const DEFAULT_MARKET: Market = "se";

const ROOF_SYSTEMS: Record<RoofSystemId, RoofSystemDefinition> = {
	felt_roof: {
		allowedKinds: ["mono", "gable"],
		layerLabelKey: "roof_systemFelt",
		coveringMaterial: "Ytpapp",
		coveringDimension: "1-lag tätskikt",
	},
	sheet_metal: {
		allowedKinds: ["mono", "gable"],
		layerLabelKey: "roof_systemSheetMetal",
		coveringMaterial: "Bandtäckt takplåt",
		coveringDimension: "Prefalsad/plåt",
	},
};

export function normalizeRoofSystemId(
	systemId?: RoofSystemId | null,
): RoofSystemId {
	return systemId === "sheet_metal" ? "sheet_metal" : "felt_roof";
}

// "gutter_kit" (roof gutters/downpipes) was removed 2026-07-16 — its 3D
// geometry looked odd and it added little value. RoofRunoffId still accepts
// the legacy value so old saved designs/share links parse without crashing,
// but this normalizer is the single upstream gate that always collapses it
// to "free_drip" (no BOM rows, no build-guide accessory copy). It is now an
// always-constant result, so it intentionally takes no arguments — see
// docs/notes/2026-06-09-engine-deprecation-without-code-removal.md.
export function normalizeRoofRunoffId(): RoofRunoffId {
	return "free_drip";
}

function getMetrics(
	width: number,
	length: number,
	roofConfig: RoofConfig,
): RoofOutlineMetrics {
	if (roofConfig.kind === "barrel") {
		return {
			slopedAreaSqm: width * length,
			slopedRunM: length,
			halfSlopeRunM: width / 2,
			takfotsplatLm: 0,
			vindskiveplatLm: 0,
			nockbeslagLm: 0,
		};
	}

	const pitchRad = (roofConfig.pitchDeg * Math.PI) / 180;

	// The 3D builder draws every roof plane with a 0.2 m overhang on all
	// sides ((W+0.4) × (L+0.4)); decking/underlay/covering areas must cover
	// that plane, not the wall-line footprint — the plan area under-bought
	// råspont/papp 12-20% despite the waste factor.
	const overhangM = 0.2;

	if (roofConfig.kind === "gable") {
		const halfSlopeRunM = width / 2 / Math.cos(Math.max(pitchRad, 0.0001));
		// addSlopedBox SHEARS (its width arg IS the horizontal run), so the
		// renderer's overhang (0.2 m, added to the HORIZONTAL half-width
		// before shearing) grows along the slope too: the along-slope
		// overhang is 0.2/cos(pitch), not a flat 0.2 m. Adding 0.2 m AFTER
		// projecting onto the slope under-counts every roof layer, worse at
		// steeper pitches (0.2 m vs 0.283 m of actual slope length at 45°).
		const halfSlopeRunWithOverhangM =
			(width / 2 + overhangM) / Math.cos(Math.max(pitchRad, 0.0001));
		return {
			slopedAreaSqm: 2 * (length + 2 * overhangM) * halfSlopeRunWithOverhangM,
			slopedRunM: length,
			halfSlopeRunM,
			takfotsplatLm: 2 * (length + 2 * overhangM),
			vindskiveplatLm: 4 * halfSlopeRunWithOverhangM,
			nockbeslagLm: length + 2 * overhangM,
		};
	}

	// Mono: the fall runs over the slope run (length for a front/back high
	// wall, width for a left/right one); the drip-edge flashing follows the
	// LOW eave, the vindskiveplåt the two raked edges.
	const slope = resolveMonoSlope(width, length, roofConfig);
	const runM = slope?.runLength ?? length;
	const eaveM = slope?.eaveLength ?? width;
	const slopedRunM = runM / Math.cos(Math.max(pitchRad, 0.0001));
	return {
		slopedAreaSqm:
			((eaveM + 2 * overhangM) * (runM + 2 * overhangM)) /
			Math.cos(Math.max(pitchRad, 0.0001)),
		slopedRunM,
		halfSlopeRunM: eaveM / 2,
		takfotsplatLm: eaveM + 2 * overhangM,
		vindskiveplatLm: 2 * (runM + 2 * overhangM) / Math.cos(pitchRad),
		nockbeslagLm: 0,
	};
}

export function getRoofSystemLabel(
	systemId: RoofSystemId,
	market: Market = DEFAULT_MARKET,
): string {
	const t = getT(market);
	return t[
		ROOF_SYSTEMS[normalizeRoofSystemId(systemId)].layerLabelKey
	] as string;
}

// Free drip is the only surfaced runoff option now (gutter_kit removed
// 2026-07-16); kept as a function of RoofRunoffId for API stability at
// existing call sites and legacy-value tolerance.
export function getRoofRunoffLabel(
	_runoff: RoofRunoffId,
	market: Market = DEFAULT_MARKET,
): string {
	const t = getT(market);
	return t.roof_runoffFreeDrip;
}

export function buildRoofAssembly({
	width,
	length,
	roofConfig,
	market = DEFAULT_MARKET,
}: BuildRoofAssemblyInput): RoofAssemblyResult | undefined {
	if (roofConfig.kind === "barrel") {
		return undefined;
	}

	const t = getT(market);
	const metrics = getMetrics(width, length, roofConfig);
	const systemId = normalizeRoofSystemId(roofConfig.systemId);
	const runoff = normalizeRoofRunoffId();
	const system = ROOF_SYSTEMS[systemId];

	if (!system.allowedKinds.includes(roofConfig.kind)) {
		return {
			status: "manual_review",
			kind: roofConfig.kind,
			systemId,
			runoff,
			slopedAreaSqm: metrics.slopedAreaSqm,
			layers: [],
			requiredAccessories: [],
			missingItems: [t.roof_manualReviewUnsupported],
			notes: [t.roof_manualReviewUnsupported],
		};
	}

	const layers = [
		t.roof_layerDecking,
		t.roof_layerUnderlay,
		getRoofSystemLabel(systemId, market),
	];

	const requiredAccessories = [
		t.roof_accessoryEaveFlashing,
		t.roof_accessoryVergeFlashing,
		...(roofConfig.kind === "gable" ? [t.roof_accessoryRidgeCapping] : []),
	];

	const notes = [
		t.roof_noteExternalOnly,
 t.construction_roofReview,
		...(roofConfig.kind === "mono" && roofConfig.pitchDeg < 14
			? [t.roof_noteLowSlope]
			: []),
	];

	return {
		status: "manual_review",
		kind: roofConfig.kind,
		systemId,
		runoff,
		slopedAreaSqm: Number(metrics.slopedAreaSqm.toFixed(1)),
		layers,
		requiredAccessories,
		missingItems: [t.construction_roofReview],
		notes,
	};
}

export function buildRoofMaterialRows({
	width,
	length,
	roofConfig,
}: Omit<BuildRoofAssemblyInput, "market">): MaterialRow[] {
	if (roofConfig.kind === "barrel") {
		return [];
	}

	const metrics = getMetrics(width, length, roofConfig);
	const areaWithWaste = Number(
		(metrics.slopedAreaSqm * WASTE_FACTOR).toFixed(1),
	);
	const systemId = normalizeRoofSystemId(roofConfig.systemId);
	const rows: MaterialRow[] = [
		{
			material: "Råspont",
			dimension: `${ROOF_DECKING_THICKNESS_MM} mm`,
			quantity: `${areaWithWaste} m²`,
			phase: "roofExterior",
			category: "roof.decking",
			priceRuleId: "prices.raspont",
			materialKey: "roof.decking",
		},
		{
			material: "Underlagspapp",
			dimension: "Takunderlag",
			quantity: `${areaWithWaste} m²`,
			phase: "roofExterior",
			category: "roof.underlay",
			priceRuleId: "prices.underlagspapp",
			materialKey: "roof.underlay",
		},
		{
			material: ROOF_SYSTEMS[systemId].coveringMaterial,
			dimension: ROOF_SYSTEMS[systemId].coveringDimension,
			quantity: `${areaWithWaste} m²`,
			phase: "roofExterior",
			category: "roof.covering",
			priceRuleId:
				systemId === "sheet_metal" ? "prices.takplat" : "prices.ytpapp",
			materialKey: "roof.covering",
		},
		{
			material: "Takfotsplåt",
			dimension: "Lackerad plåt",
			quantity: `${metrics.takfotsplatLm.toFixed(1)} lpm`,
			phase: "roofExterior",
			category: "roof.flashings",
			priceRuleId: "prices.takfotsplat",
			materialKey: "roof.eave-flashing",
		},
		{
			material: "Vindskiveplåt",
			dimension: "Lackerad plåt",
			quantity: `${metrics.vindskiveplatLm.toFixed(1)} lpm`,
			phase: "roofExterior",
			category: "roof.flashings",
			priceRuleId: "prices.vindskiveplat",
			materialKey: "roof.verge-flashing",
		},
	];

	if (roofConfig.kind === "gable") {
		rows.push({
			material: "Nockbeslag",
			dimension: "Lackerad plåt",
			quantity: `${metrics.nockbeslagLm.toFixed(1)} lpm`,
			phase: "roofExterior",
			category: "roof.flashings",
			priceRuleId: "prices.nockbeslag",
			materialKey: "roof.ridge-capping",
		});
	}

	return rows;
}
